import React, { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { addDays, today } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Property, Task, TaskType } from '../lib/types';
import { Drawer, TaskRow, type Ctx } from '../main';

export function Tasks({ ctx, tasks, properties }: {
  ctx: Ctx; tasks: Task[]; properties: Property[];
}): React.JSX.Element {
  const [filter, setFilter] = useState('all');
  const [property, setProperty] = useState('all');
  const [who, setWho] = useState('all');

  const workflows = [...new Set(tasks.map((t) => t.workflow))].sort();
  let list = tasks;
  if (filter === 'late') list = list.filter((t) => t.dueDate < today());
  else if (filter === 'money') list = list.filter((t) => t.type !== 'Action');
  else if (filter === 'started') list = list.filter((t) => t.status === 'InProgress');
  else if (filter !== 'all') list = list.filter((t) => t.workflow === filter);
  if (property !== 'all') list = list.filter((t) => t.propertyId === property);
  if (who === 'none') list = list.filter((t) => !t.assigneeId);
  else if (who !== 'all') list = list.filter((t) => t.assigneeId === who);
  list = [...list].sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));

  return (
    <>
      <div className="bar">
        {(['all', 'late', 'started', 'money'] as const).map((f) => (
          <button key={f} className={`btn sm ${filter === f ? 'pri' : ''}`} onClick={() => setFilter(f)}>
            {{ all: 'All', late: 'Overdue', started: 'In progress', money: 'Money' }[f]}
          </button>
        ))}
        {workflows.map((w) => (
          <button key={w} className={`btn sm ${filter === w ? 'pri' : ''}`} onClick={() => setFilter(w)}>{w}</button>
        ))}
      </div>
      <div className="bar">
        <select className="mini" value={property} onChange={(e) => setProperty(e.target.value)} aria-label="Property">
          <option value="all">Every site</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="mini" value={who} onChange={(e) => setWho(e.target.value)} aria-label="Assigned to">
          <option value="all">Everyone</option>
          <option value="none">Unassigned</option>
          {ctx.employees.filter((e) => e.is_active).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <span className="w" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn sm danger" onClick={() => ctx.newTask({ emergency: true })}>Report an emergency</button>
          <button className="btn sm accent" onClick={() => ctx.newTask()}>New task</button>
        </span>
      </div>

      {list.map((t) => <TaskRow key={t.id} task={t} ctx={ctx} />)}
      {list.length === 0 && (
        <div className="empty"><b>Nothing here</b>No open task matches that filter.</div>
      )}
    </>
  );
}

/* --------------------------------------------------------------- creating */

export interface NewTaskPreset { emergency?: boolean; propertyId?: string; roomId?: string }

interface Choice { name: string; workflow: string; type: TaskType; entry: boolean }

/** Who reported it. The values are the API's; the words are ours. */
const SOURCES: Array<[string, string]> = [
  ['TenantCall', 'Tenant, by phone'], ['TenantApp', 'Tenant app'], ['Operations', 'Our own team'],
  ['CameraAlert', 'Camera alert'], ['Landlord', 'Landlord'],
];
/** What is happening. A label for the record and the dispatch message, not a rule. */
const CATEGORIES = ['Water leak', 'Electrical', 'Fire or smoke', 'Gas smell', 'Lock-out',
  'Lift or access', 'Structural', 'Pests', 'Security', 'Other'];
/** Due date by severity: the response time the team works to. */
const DUE_IN: Record<string, number> = { Critical: 0, High: 1, Normal: 3 };

/**
 * Creating work by hand.
 *
 * The list of what may be created comes from the catalogue: entry points to the
 * automated workflows, plus the ad-hoc types. Everything else is created by the
 * engine as a consequence of completing something, and the API refuses it here.
 *
 * An emergency is the same form with the Emergency workflow's entry point chosen
 * and three more fields. The entry point is found by its workflow, never by its
 * task name — the name belongs to the engine.
 */
export function NewTask({ ctx, preset, onClose, onCreated }: {
  ctx: Ctx; preset: NewTaskPreset; onClose: () => void;
  onCreated: (task: Task, title: string) => Promise<void>;
}): React.JSX.Element {
  // A specialist reports what they find — an emergency, or ad-hoc work — and
  // starts nothing that commits money or a tenancy. The API holds the same line;
  // this keeps the form from offering what it would refuse.
  const choices = useMemo<Choice[]>(() => [
    ...ctx.catalogue.rules.filter((r) => r.manualEntry && (ctx.canManage || r.workflow === 'Emergency'))
      .map((r) => ({ name: r.name, workflow: r.workflow, type: r.type, entry: true })),
    ...Object.entries(ctx.catalogue.adhoc).flatMap(([workflow, items]) =>
      items.filter((i) => ctx.canManage || i.type === 'Action')
        .map((i) => ({ name: i.name, workflow, type: i.type, entry: false }))),
  ], [ctx.catalogue, ctx.canManage]);
  const emergencyEntry = choices.find((c) => c.entry && c.workflow === 'Emergency') ?? null;

  const [choice, setChoice] = useState<Choice | null>(preset.emergency ? emergencyEntry : null);
  const [propertyId, setPropertyId] = useState(preset.propertyId ?? ctx.properties[0]?.id ?? '');
  const [roomId, setRoomId] = useState(preset.roomId ?? '');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState(today());
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [severity, setSeverity] = useState('High');
  const [source, setSource] = useState('TenantCall');
  const [category, setCategory] = useState(CATEGORIES[0]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rooms = useResource(
    () => (propertyId ? api.rooms(propertyId) : Promise.resolve({ rooms: [] })), [propertyId]);
  const isEmergency = choice?.workflow === 'Emergency';
  // a specialist raises work for themselves or for triage, not for a colleague
  const people = ctx.employees.filter((e) => e.is_active && (ctx.canManage || e.id === ctx.me.id));

  const pickSeverity = (s: string): void => {
    setSeverity(s);
    setDueDate(addDays(today(), DUE_IN[s] ?? 3));
  };

  const create = async (): Promise<void> => {
    if (!choice) return;
    setBusy(true); setError(null);
    try {
      const body: Record<string, unknown> = {
        name: choice.name, workflow: choice.workflow, propertyId, dueDate,
        ...(roomId ? { roomId } : {}),
        ...(assigneeId ? { assigneeId } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(choice.type !== 'Action' && Number(amount) > 0 ? { amount: Number(amount) } : {}),
        ...(isEmergency ? { severity, source, category } : {}),
      };
      const { task } = await api.createTask(body);
      await onCreated(task, isEmergency ? 'Emergency raised' : 'Task created');
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title={preset.emergency ? 'Report an emergency' : 'New task'}
      subtitle={preset.emergency
        ? 'Opens the Emergency workflow and puts it at the top of someone’s list'
        : 'Only entry points and ad-hoc work can be created by hand'}
      onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className={`btn ${isEmergency ? 'danger' : 'accent'}`}
          disabled={!choice || !propertyId || busy} onClick={create}>
          {busy ? 'Creating…' : isEmergency ? 'Raise emergency' : 'Create'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}

      {isEmergency ? (
        <div className="note bad">
          If anyone is in danger, call <b>999</b> first. This logs the incident and dispatches the
          team; it is not an emergency service.
        </div>
      ) : (
        <div className="note">
          Everything else is created by the engine when something else completes. The API refuses a
          task that is meant to arrive that way, which is what keeps a chain honest.
        </div>
      )}

      <div className="field" style={{ marginTop: 14 }}>
        <label htmlFor="site">Property</label>
        <select id="site" value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setRoomId(''); }}>
          {ctx.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {ctx.properties.length === 0 && <div className="hint">Add a property first.</div>}
      </div>
      <div className="frow">
        <div className="field">
          <label htmlFor="room">Room or area</label>
          <select id="room" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            <option value="">— whole property —</option>
            {(rooms.data?.rooms ?? []).map((r) => (
              <option key={r.id} value={r.id}>Room {r.number} · {r.status}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="who">{isEmergency ? 'Dispatch to' : 'Assigned to'}</label>
          <select id="who" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">— leave for triage —</option>
            {people.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.role}</option>)}
          </select>
        </div>
      </div>

      {isEmergency && (
        <>
          <div className="frow">
            <div className="field">
              <label htmlFor="cat">What is happening?</label>
              <select id="cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="sev">Severity</label>
              <select id="sev" value={severity} onChange={(e) => pickSeverity(e.target.value)}>
                {Object.keys(DUE_IN).map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="src">Reported by</label>
            <select id="src" value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="hint">Critical is due today, High tomorrow, Normal in three days.</div>
          </div>
        </>
      )}

      <div className="frow">
        <div className="field">
          <label htmlFor="due">Due</label>
          <input id="due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        {choice && choice.type !== 'Action' && (
          <div className="field">
            <label htmlFor="amt">Expected amount (AED)</label>
            <input id="amt" type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
        )}
      </div>
      <div className="field">
        <label htmlFor="desc">{isEmergency ? 'What was reported' : 'Notes'}</label>
        <textarea id="desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder={isEmergency ? 'Exactly what the resident described, plus access notes and a contact on site' : ''} />
      </div>

      {!preset.emergency && (
        <>
          <h3 className="sec" style={{ fontSize: 13 }}>Start a workflow</h3>
          {choices.filter((c) => c.entry).map((c) => (
            <button key={c.name} className={`opt ${choice?.name === c.name ? 'on' : ''}`} onClick={() => setChoice(c)}>
              <span className="k" />
              <span><b>{c.name}</b><span>{c.workflow} · {c.type}</span></span>
            </button>
          ))}
          {Object.keys(ctx.catalogue.adhoc).map((workflow) => (
            <div key={workflow}>
              <h3 className="sec" style={{ fontSize: 13 }}>{workflow}</h3>
              {choices.filter((c) => !c.entry && c.workflow === workflow).map((c) => (
                <button key={c.name} className={`opt ${choice?.name === c.name && choice.workflow === workflow ? 'on' : ''}`}
                  onClick={() => setChoice(c)}>
                  <span className="k" />
                  <span><b>{c.name}</b><span>{c.type}</span></span>
                </button>
              ))}
            </div>
          ))}
        </>
      )}
      {preset.emergency && !emergencyEntry && (
        <div className="note bad">The engine serves no Emergency entry point. Check GET /v1/workflows.</div>
      )}
    </Drawer>
  );
}
